import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Clock, Users, FileText, MessageCircle } from "lucide-react";

interface StatusBadgeProps {
  type: 'active-guards' | 'pending-reports' | 'unread-messages' | 'active-shifts';
  count: number;
  showZero?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'secondary';
}

const badgeConfig = {
  'active-guards': {
    icon: Users,
    label: 'Active',
    color: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    pulseColor: 'bg-emerald-500',
  },
  'pending-reports': {
    icon: FileText,
    label: 'Pending',
    color: 'bg-amber-500/10 text-amber-700 border-amber-200',
    pulseColor: 'bg-amber-500',
  },
  'unread-messages': {
    icon: MessageCircle,
    label: 'New',
    color: 'bg-blue-500/10 text-blue-700 border-blue-200',
    pulseColor: 'bg-blue-500',
  },
  'active-shifts': {
    icon: Clock,
    label: 'Shifts',
    color: 'bg-purple-500/10 text-purple-700 border-purple-200',
    pulseColor: 'bg-purple-500',
  },
};

export const StatusBadge = ({ 
  type, 
  count, 
  showZero = false, 
  size = 'sm',
  variant = 'outline' 
}: StatusBadgeProps) => {
  if (!showZero && count === 0) return null;

  const config = badgeConfig[type];
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'h-6 px-2 text-xs',
    md: 'h-7 px-3 text-sm',
    lg: 'h-8 px-4 text-sm',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  };

  return (
    <div className="relative">
      <Badge 
        variant={variant}
        className={cn(
          "flex items-center gap-1.5 font-medium transition-all duration-200",
          sizeClasses[size],
          config.color,
          count > 0 && "shadow-sm"
        )}
      >
        <Icon className={iconSizes[size]} />
        <span className="font-semibold">{count}</span>
        <span className="hidden sm:inline">{config.label}</span>
      </Badge>
      
      {/* Animated pulse for active items */}
      {count > 0 && type === 'active-guards' && (
        <div className={cn(
          "absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse",
          config.pulseColor
        )} />
      )}
    </div>
  );
};